import React, { useRef, useEffect } from 'react';
import {
    useSsDispatch as useDispatch,
    useSsSelector as useSelector,
} from '~SpectateStore';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import {
    setSiteScrollDisabled,
} from '~spa/Utils/WrapperIntegration/Ucf';

import {
    getPriceOdds,
    getOddsFromSelection,
} from '~shared/components/sport-event/utils.js';

import {
    setFullScreenOn,
} from '~Betslip/slices/uiSlice';

import {
    updateSelection,
} from '~Betslip/slices/userSelectionsSlice';

import {
    updateUserParlay,
} from '~Betslip/slices/parlayTabSlice';

import {
    selectCountUserSelections,
    selectUserSelections,
    selectAnySelectionReverseCast,
    selectHasAnySpSelected,
    selectAnySelectionIsCastMarket,
} from '~Betslip/selectors/userSelectionsSelectors';

import {
    selectShouldShowOpenBetslip,
} from '~Betslip/selectors/userSelectors';

import {
    selectIsPlacingBet,
    selectIsRefreshingBetslipData,
} from '~Betslip/selectors/uiSelectors';

import {
    selectParlay,
    selectUserParlay,
    selectPotentialReturnsMultiplier,
} from '~Betslip/selectors/parlaySelectors';

import {
    selectAnySelectionSuspended,
    selectSelectionDetailsById,
} from '~Betslip/selectors/selectionDetailsSelectors';

import {
    useKeyboard,
} from '~Betslip/store/keyboardContext';

import {
    selectBabyStake,
} from '~Betslip/selectors/totalStakeSelectors';

import { selectAvailableSingle } from '~Betslip/selectors/availableBetTypesSelectors';

import { selectMultiplesSelectionDetails } from '~Betslip/selectors/userMultiplesSelectors';

import {
    isDepositNeeded,
} from '~Betslip/utils/depositChecks';

import {
    selectShowPriceChange, selectShowPriceChangeNotification,
} from '~Betslip/selectors/notificationSelectors';

import {
    selectShowContingenciesNotification,
} from '~Betslip/selectors/contingenciesSelectors';

import {
    hasZeroBalance,
} from '~Betslip/utils/depositChecks';

import {
    useIsEachwayAllowed,
    useOddsFormat,
    useShouldHideLogin,
    useUserBalance,
} from '~SpectateStore/hooks/userHooks';

import {
    updateBabyStake,
} from '~Betslip/slices/babyBetslipSlice';

import { DECIMAL_ODDS_FORMAT } from '~shared/utils/constants';
import { getDecimalPlaces } from '~Betslip/utils/numberFormatters';

import Notifications from '../Full/Components/Notifications';
import StakeField from '../Full/Components/StakeField';
import CallToActions from '../Full/Components/CallToActions';
import BetDescriptionIcons from './components/BetDescriptionIcons/BetDescriptionIcons';

import './styles/baby.scss';

const Baby = () => {
    const [t] = useTranslation();
    const dispatch = useDispatch();
    const shouldHideLogin = useShouldHideLogin();
    const isPlacingBet = useSelector(selectIsPlacingBet);
    const oddsFormat = useOddsFormat();
    const isUserEachwayAllowed = useIsEachwayAllowed();

    const babyStake = useSelector(selectBabyStake);
    const count = useSelector(selectCountUserSelections);
    const parlay = useSelector(selectParlay);
    const single = useSelector(selectAvailableSingle);
    const anySelectionSuspended = useSelector(selectAnySelectionSuspended);
    const potentialReturnsMultiplier = useSelector(selectPotentialReturnsMultiplier);
    const isStartingPrice = useSelector(selectHasAnySpSelected);
    const showContingenciesNotification = useSelector(selectShowContingenciesNotification);

    const parlayUserBetDetail = useSelector(selectUserParlay);
    const parlaySelectionDetails = useSelector(selectMultiplesSelectionDetails);

    const singleUserBetDetail = useSelector(selectUserSelections)[0]; //get the only user selection available
    const singleSelectionDetails = useSelector((state) => selectSelectionDetailsById(state, singleUserBetDetail?.id));
    const isRefreshingBetslipData = useSelector(selectIsRefreshingBetslipData);

    const { priceChangeDirection } = useSelector((state) => selectShowPriceChange(state, singleUserBetDetail?.id));
    const priceChanged = useSelector(selectShowPriceChangeNotification);

    const userBalance = useUserBalance();
    const isEmptyBalance = hasZeroBalance(userBalance);
    const userBetDetail = parlay ? parlayUserBetDetail : singleUserBetDetail;
    const userStake = userBetDetail?.stake || babyStake;
    const betDetail = parlay ? parlay : singleSelectionDetails;
    const betslipId = parlay ? parlay?.id : singleUserBetDetail?.id;
    const isCastMarket = singleSelectionDetails ? singleSelectionDetails.is_cast_market : false;
    const isCastMarketAny = useSelector(selectAnySelectionReverseCast);
    const isAnySelectionIsCastMarket = useSelector(selectAnySelectionIsCastMarket);

    const isFreeBetSelected = userBetDetail?.free_bet_id || 0;

    const isEachWayAllowed = betDetail?.each_way_allowed && isUserEachwayAllowed;
    const showOpenBabyslip = useSelector(selectShouldShowOpenBetslip);
    const showStakeField = userBetDetail && !showOpenBabyslip && !isPlacingBet;
    const displayEachWayTerms = isEachWayAllowed && single;

    const { setKeyboardContainerRef, closeKeyboard, keyboardContainerRef } = useKeyboard();
    const keyboardRef = useRef();

    const hasParlay = parlay !== undefined;

    //this effect is used only to sync BabyBetslip stake with user single and parlay stake
    useEffect(() => {
        if (hasParlay) {
            dispatch(updateUserParlay({ stake: babyStake }));
        } else {
            dispatch(updateSelection({
                id: userBetDetail?.id,
                stake: babyStake,
            }));
        }
    }, [count, hasParlay]);

    useEffect(() => {
        if (showContingenciesNotification) {
            closeKeyboard();
        }
    }, [showContingenciesNotification]);

    const enterFullScreenBetslip = () => {
        if (!isPlacingBet) {
            dispatch(setFullScreenOn());
            setSiteScrollDisabled(true);
        }
    };

    const renderAvailableBetType = () => {
        if (showContingenciesNotification || (!single && isAnySelectionIsCastMarket)) {
            return t('betslip.selections');
        }

        if (parlay) {
            const betName = parlay.name;
            const specialCases = ['Doubles', 'Trebles'];
            if (!specialCases.includes(betName)) {
                return t(`bet_types.name.${betName}`);
            }

            return t(`betslip.acca_${betName}`);
        }

        if (isCastMarket) {
            return singleSelectionDetails.market_name;
        }

        return t('betslip.single');
    };

    const getOddsPrice = () => {
        if (single && !isStartingPrice) {
            const odds = getOddsFromSelection(singleSelectionDetails);

            // The odds variable can have the values: SP or the price in the
            // current odds format. E.g: 1/100 or 1.01, therefore we need to
            // check if it's not SP and if the odds format is decimal and
            // then format it properly, otherwise, we need to return the
            // first odds price from the selection
            if (oddsFormat === DECIMAL_ODDS_FORMAT) {
                return getPriceOdds(odds, oddsFormat, getDecimalPlaces(odds));
            }

            return odds;
        }

        if (potentialReturnsMultiplier && !isStartingPrice) {
            return getPriceOdds(potentialReturnsMultiplier, oddsFormat, getDecimalPlaces(potentialReturnsMultiplier));
        }

        return t('betslip.sp');
    };

    const renderOdds = () => {
        if (showContingenciesNotification || anySelectionSuspended) {
            return null;
        }

        const oddsClasses = classNames('babyslip__odds', {
            'babyslip__odds-change': priceChanged,
            [`babyslip__odds-change--${priceChangeDirection}`]: single && priceChangeDirection,
        });

        return (
            <span className={oddsClasses}>
                {getOddsPrice()}
            </span>
        );
    };

    const renderEwTerms = () => {

        if (!displayEachWayTerms) {
            return null;
        }

        const { place_odds_divisor, places_paid } = singleSelectionDetails;

        return (
            <div className='babyslip__ew-terms' dir="auto">
                {t('racing.ew_terms')}:
                <span dir="auto">
                    {` ${t('racing.ew_conditions', {
                        divisor: place_odds_divisor,
                        places: places_paid,
                    })}`}
                </span>
            </div>
        );
    };

    const multiSelectionNames = () => {
        const selectionNames = parlaySelectionDetails.map((selection, index) => {
            let selectionName = selection.selection_long_name === null || isCastMarketAny
                ? selection.selection_name
                : selection.market_name;

            if (selection.is_match_combo) {
                selectionName = t('betslip.match_combo');
            }

            if (count - index > 1) {
                selectionName = `${selectionName}, `;
            }

            return selectionName;
        });

        return selectionNames;
    };

    const singleSelectionName = () => {
        if (isCastMarket) {
            const selectionNames = singleSelectionDetails.selection_names;

            return selectionNames.map((name, index) => (
                <span key={index}>{name}
                    <span className="babyslip__selection-hint">
                        {' '}
                        {t(`betslip.fctc_header_${index + 1}`)}
                    </span>
                    {index < selectionNames.length - 1 ? ', ' : ''}
                </span>
            ));
        }

        return singleSelectionDetails.selection_long_name === null
            ? singleSelectionDetails.selection_name
            : singleSelectionDetails.market_name;
    };

    const renderSelectionName = () => {
        const selectionName = single
            ? singleSelectionName()
            : multiSelectionNames();

        const selectionClasses = classNames('babyslip__selection-name',
            {
                'babyslip__selection-name--one-line': displayEachWayTerms,
            });

        return (
            <div className={selectionClasses}>
                {selectionName}
            </div>
        );
    };

    const checkDepositNeeded = () => {
        if (isEmptyBalance && !isFreeBetSelected) {
            return true;
        }

        let stake = userStake;
        if (userBetDetail.each_way_selected) {
            stake = stake * 2;
        }

        return isDepositNeeded(userBalance, stake);
    };

    const openKeyboard = () => {
        if (!keyboardContainerRef) {
            updateUserChoices('', userBetDetail.each_way_selected, null);
        }
        setKeyboardContainerRef(keyboardRef.current);
    };

    const updateUserChoices = (stake, each_way_selected, free_bet_id) => {
        const fbStake = (free_bet_id) ? 0 : stake;
        const data = { stake: fbStake, each_way_selected, free_bet_id };
        if (parlay) {
            dispatch(updateUserParlay(data));
        } else {
            dispatch(updateSelection({
                id: userBetDetail?.id,
                ...data,
            }));
        }
        dispatch(updateBabyStake(fbStake));
    };

    const openFullslipClass = classNames('babyslip__openFullslip',
    {
        'babyslip__openFullslip--placing-bet': isPlacingBet,
    });

    const openBetslipCTA = () => {
        return (
            <div
                onClick={enterFullScreenBetslip}
                className={openFullslipClass}
                data-test-id="baby-betslip;expand-betslip"
                data-spectate-product="new-baby-betslip;expand-betslip"
            >
                <span>{t('betslip.betslip')}</span>
                <span className="babyslip__arrow"></span>
            </div>
        );
    };

    const betslipCounter = () => {
        return (
            <div
                className="babyslip__counter-container"
            >
                {count > 0 &&
                    <span
                        className="babyslip__counter-container__counter"
                        data-test-id="baby-betslip;counter">
                        {count}
                    </span>
                }
            </div>
        );
    };

    if (shouldHideLogin) {
        return <div className="babyslip babyslip__no-betslip">
            <i className="fullslip__no-betslip-icon"></i>
            <span>{t('betslip.app_download')}</span>
        </div>;
    }

    const gtmDetails = {
        source: 'new-baby-betslip',
        betType: betslipId,
    };

    const showLoader = isRefreshingBetslipData || (!betDetail && count > 0);
    const showBody = !showLoader && count > 0;

    const babyslipBody = () => {

        return <>
            <div className="babyslip__stake-section">
                {showLoader &&
                    <span className="babyslip__loading" />
                }
                {showBody &&
                    <div>
                        <div className="babyslip__odds-type-container">
                            <span className="babyslip__type">
                                {renderAvailableBetType()}
                            </span>
                            {renderOdds()}
                            {single
                                ? <BetDescriptionIcons selectionId={singleUserBetDetail?.id} />
                                : null
                            }
                        </div>
                        {renderSelectionName()}
                        {renderEwTerms()}
                    </div>
                }
                {
                    showStakeField
                        ? <StakeField
                            stake={userStake}
                            each_way_allowed={isEachWayAllowed}
                            each_way_selected={userBetDetail.each_way_selected}
                            onChangeCallback={updateUserChoices}
                            onClickHandler={openKeyboard}
                            freebetId={userBetDetail.free_bet_id}
                            betslipId={betslipId}
                            gtmDetails={gtmDetails}
                            depositRequired={checkDepositNeeded()}
                        />
                        : null
                }
            </div>
        </>;
    };

    return (
        <>
            <Notifications />
            {openBetslipCTA()}
            <div className="babyslip__keyboard-container" ref={keyboardRef} />
            <div className="babyslip">
                {betslipCounter()}
                <div className="babyslip__details">
                    {babyslipBody()}
                </div>
            </div>
            <CallToActions />
        </>
    );
};

export default Baby;
